import React, { useState, useEffect } from "react";

function App() {
  // 1. Grading Scales Config
  const gradingScales = {
    "10-point": { min: 0, max: 10, step: 0.1 },
    "4-point": { min: 0, max: 4, step: 0.1 }
  };

  const [scale, setScale] = useState("10-point");

  // 2. State: Subjects, CGPA, Errors
  const [subjects, setSubjects] = useState(() => {
    const saved = localStorage.getItem("subjects");
    if (saved) return JSON.parse(saved);
    return [{ grade: "", credit: "" }];
  });

  const [cgpa, setCgpa] = useState(null);
  const [errors, setErrors] = useState([]);

  // 3. Persist subjects to localStorage
  useEffect(() => {
    localStorage.setItem("subjects", JSON.stringify(subjects));
  }, [subjects]);

  // 4. Handlers for input, add, remove, reset
  const updateSubject = (idx, field, value) => {
    const updated = subjects.map((s, i) =>
      i === idx ? { ...s, [field]: value } : s
    );
    setSubjects(updated);
  };

  const addSubject = () => {
    setSubjects([...subjects, { grade: "", credit: "" }]);
  };

  const removeSubject = idx => {
    const updated = subjects.filter((_, i) => i !== idx);
    setSubjects(updated.length ? updated : [{ grade: "", credit: "" }]);
  };

  const resetAll = () => {
    setSubjects([{ grade: "", credit: "" }]);
    setCgpa(null);
    setErrors([]);
    localStorage.removeItem("subjects");
  };

  // 5. Validation
  const validateInputs = () => {
    let err = [];
    subjects.forEach((s, idx) => {
      if (s.grade === "" || s.credit === "") {
        err.push(`Subject ${idx + 1}: Grade and Credit are required.`);
      } else {
        const grade = parseFloat(s.grade);
        const credit = parseFloat(s.credit);
        if (isNaN(grade) || grade < gradingScales[scale].min || grade > gradingScales[scale].max) {
          err.push(
            `Subject ${idx + 1}: Grade must be between ${gradingScales[scale].min} and ${gradingScales[scale].max}.`
          );
        }
        if (isNaN(credit) || credit <= 0) {
          err.push(`Subject ${idx + 1}: Credit must be a positive number.`);
        }
      }
    });
    setErrors(err);
    return err.length === 0;
  };

  // 6. Calculate CGPA
  const calculateCGPA = () => {
    if (!validateInputs()) {
      setCgpa(null);
      return;
    }
    let totalPoints = 0, totalCredits = 0;
    subjects.forEach(({ grade, credit }) => {
      totalPoints += parseFloat(grade) * parseFloat(credit);
      totalCredits += parseFloat(credit);
    });
    if (totalCredits > 0) setCgpa((totalPoints / totalCredits).toFixed(2));
  };

  // 7. JSX UI
  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>CGPA Calculator</h2>

      <div style={styles.scaleSelector}>
        <label htmlFor="gradingScale">Choose Grading Scale: </label>
        <select
          id="gradingScale"
          value={scale}
          onChange={e => setScale(e.target.value)}
          style={styles.select}
        >
          {Object.keys(gradingScales).map(scaleKey => (
            <option key={scaleKey} value={scaleKey}>
              {scaleKey}
            </option>
          ))}
        </select>
        <div style={{ fontSize: "0.95em", marginTop: 6, color: "#666" }}>
          Use <b>10-point</b> for Indian/CBSE/engineering pattern; <b>4-point</b> for US/Canadian colleges.
        </div>
      </div>

      {subjects.map((s, idx) => (
        <div key={idx} style={styles.subjectRow}>
          <input
            type="number"
            placeholder={`Grade (0-${gradingScales[scale].max})`}
            value={s.grade}
            min={gradingScales[scale].min}
            max={gradingScales[scale].max}
            step={gradingScales[scale].step}
            onChange={e => updateSubject(idx, "grade", e.target.value)}
            style={styles.input}
          />
          <input
            type="number"
            placeholder="Credit"
            value={s.credit}
            min="0.1"
            step="0.1"
            onChange={e => updateSubject(idx, "credit", e.target.value)}
            style={styles.input}
          />
          <button onClick={() => removeSubject(idx)} style={styles.removeButton} aria-label={`Remove subject ${idx + 1}`}>
            &times;
          </button>
        </div>
      ))}
      <div style={styles.buttons}>
        <button onClick={addSubject} style={styles.button}>
          Add Subject
        </button>
        <button onClick={calculateCGPA} style={styles.button}>
          Calculate
        </button>
        <button onClick={resetAll} style={styles.resetButton}>
          Reset
        </button>
      </div>

      {errors.length > 0 && (
        <div style={styles.errorBox} role="alert">
          <ul>
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {cgpa && <h3 style={styles.result}>Your CGPA is: {cgpa}</h3>}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "480px",
    margin: "auto",
    padding: "20px",
    fontFamily: "Arial, sans-serif",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
  },
  heading: {
    textAlign: "center",
    color: "#333",
  },
  scaleSelector: {
    marginBottom: "20px",
    textAlign: "center",
  },
  select: {
    padding: "6px 10px",
    fontSize: "16px",
    marginLeft: "10px",
  },
  subjectRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: "10px",
  },
  input: {
    flexGrow: 1,
    padding: "8px",
    marginRight: "10px",
    fontSize: "16px",
    borderRadius: "4px",
    border: "1px solid #ccc",
  },
  removeButton: {
    backgroundColor: "#ff4d4d",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontSize: "20px",
    cursor: "pointer",
    width: "32px",
    height: "32px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  buttons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "15px",
    gap: "10px",
    flexWrap: "wrap"
  },
  button: {
    flexGrow: 1,
    padding: "10px 0",
    backgroundColor: "#007bff",
    border: "none",
    borderRadius: "4px",
    color: "white",
    fontSize: "16px",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
  },
  resetButton: {
    flexGrow: 1,
    padding: "10px 0",
    backgroundColor: "#6c757d",
    border: "none",
    borderRadius: "4px",
    color: "white",
    fontSize: "16px",
    cursor: "pointer",
  },
  errorBox: {
    marginTop: "20px",
    backgroundColor: "#ffe6e6",
    border: "1px solid #ff4d4d",
    borderRadius: "4px",
    padding: "10px",
    color: "#b30000",
  },
  result: {
    marginTop: "20px",
    textAlign: "center",
    color: "#28a745",
  }
};

export default App;
